import React, { useContext, useState, useEffect } from 'react';
import { DataContext } from '../contexts/DataContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTruckMoving, faCheckCircle, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import logo from "../wisdom.png";
import MiniDashboard from './ShipmentGridMinidashboard';
import Pagination from '../utils/Pagination';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";


const ShipmentGrid = () => {
  const { shipments } = useContext(DataContext);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100); // Set your default page size
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredShipments, setFilteredShipments] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [originOptions, setOriginOptions] = useState([]);
  const [carrierOptions, setCarrierOptions] = useState([]);
  const [vesselOptions, setVesselOptions] = useState([]);
  const [destinationOptions, setDestinationOptions] = useState([]);
  const [consigneeOptions, setConsigneeOptions] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedOrigins, setSelectedOrigins] = useState([]);
  const [selectedConsignees, setSelectedConsignees] = useState([]);
  const [selectedDestinations, setSelectedDestinations] = useState([]);
  const [selectedCarriers, setSelectedCarriers] = useState([]);
  const [selectedVessels, setSelectedVessels] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const filterData = () => {
    const lowercasedFilter = searchTerm.toLowerCase();
    const filteredData = shipments.filter((shipment) => {
      return Object.keys(shipment).some((key) =>
        // Check if the property is not null or undefined before calling toString()
        shipment[key] != null && shipment[key].toString().toLowerCase().includes(lowercasedFilter)
      );
    });
    setFilteredShipments(filteredData);
  };

  useEffect(() => {
    const uniqueConsignees = Array.from(new Set(shipments.map(s => s.consigneeName)))
      .filter(name => name) // Filter out any null or undefined names
      .map(name => ({ label: name, value: name }));
    setConsigneeOptions(uniqueConsignees); // Assuming you've added a state for this
  }, [shipments]);

  // useEffect hook to update the filteredShipments when shipments or searchTerm changes
  useEffect(() => {
    if (shipments) {
      filterData();
    }
  }, [shipments, searchTerm]);

  useEffect(() => {
    if (shipments) {
      // Unique origins
      const uniqueStatuses = Array.from(new Set(shipments.map(s => s.jobStatus)))
        .map(status => ({ label: status, value: status }));
      setStatusOptions(uniqueStatuses);

      const origins = Array.from(
        new Set(shipments.map(s => `${s.consignorCity}, ${s.consignorState}, ${s.consignorPostCode}`))
      ).map(origin => ({ label: origin, value: origin }));
      setOriginOptions(origins);

      // Unique destinations
      const destinations = Array.from(
        new Set(shipments.map(s => `${s.consigneeCity}, ${s.consigneeState}, ${s.consigneePostCode}`))
      ).map(destination => ({ label: destination, value: destination }));
      setDestinationOptions(destinations);

      // Unique carriers
      const carriers = Array.from(
        new Set(shipments.map(s => s.carrierName))
      ).filter(carrier => carrier != null) // Ensure no null values are included
        .map(carrier => ({ label: carrier, value: carrier }));
      setCarrierOptions(carriers);

      // Unique vessels/flights
      const vessels = Array.from(
        new Set(shipments.flatMap(s => [s.vessel, s.flightVoyage].filter(Boolean)))
      ).map(vessel => ({ label: vessel, value: vessel }));
      setVesselOptions(vessels);
    }
  }, [shipments]);

  const handleOriginFilterChange = (selectedOptions) => {
    setSelectedOrigins(selectedOptions);
    applyFilters();
  };

  const handleDestinationFilterChange = (selectedOptions) => {
    setSelectedDestinations(selectedOptions);
    applyFilters();
  };

  const handleCarrierFilterChange = (selectedOptions) => {
    setSelectedCarriers(selectedOptions);
    applyFilters();
  };

  const handleVesselFilterChange = (selectedOptions) => {
    setSelectedVessels(selectedOptions);
    applyFilters();
  };
  const handleStatusFilterChange = (selectedOptions) => {
    setSelectedStatuses(selectedOptions);

    const filtered = shipments.filter(shipment =>
      selectedOptions.some(option => option.value === shipment.jobStatus)
    );

    setFilteredShipments(filtered);
  };

  const CustomDateInput = ({ value, onClick, placeholder }) => (
    <button
      className="flex items-center justify-between border border-gray-300 bg-white rounded-md px-3 py-2 w-full hover:border-blue-300 focus:outline-none focus:ring focus:border-blue-300 focus:ring-blue-200 focus:ring-opacity-50"
      onClick={onClick}
    >
      <span className="text-gray-700 mr-2">{value || placeholder}</span> {/* Added margin-right */}
      <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-500" />
    </button>
  );


  const applyFilters = () => {
    let filtered = [...shipments]; // Start with all shipments

    // Apply status filter
    if (selectedStatuses.length) {
      filtered = filtered.filter(shipment =>
        selectedStatuses.some(status => status.value === shipment.jobStatus)
      );
    }

    // Apply origin filter
    if (selectedOrigins.length) {
      filtered = filtered.filter(shipment =>
        selectedOrigins.some(origin => `${shipment.consignorCity}, ${shipment.consignorState}, ${shipment.consignorPostCode}` === origin.value)
      );
    }

    // Apply destination filter
    if (selectedDestinations.length) {
      filtered = filtered.filter(shipment =>
        selectedDestinations.some(destination => `${shipment.consigneeCity}, ${shipment.consigneeState}, ${shipment.consigneePostCode}` === destination.value)
      );
    }

    // Apply carrier filter
    if (selectedCarriers.length) {
      filtered = filtered.filter(shipment =>
        selectedCarriers.some(carrier => shipment.carrierName === carrier.value)
      );
    }

    // Apply vessel/flight filter
    if (selectedVessels.length) {
      filtered = filtered.filter(shipment =>
        selectedVessels.some(vessel => [shipment.vessel, shipment.flightVoyage].includes(vessel.value))
      );
    }

    if (startDate && endDate) {
      filtered = filtered.filter(shipment => {
        const etd = new Date(shipment.originEtd);
        return etd >= startDate && etd <= endDate;
      });
    }
    if (selectedConsignees.length) {
      filtered = filtered.filter(shipment =>
        selectedConsignees.some(consignee => consignee.value === shipment.consigneeName)
      );
    }
    // Set the filtered shipments state
    setFilteredShipments(filtered);
  };

  // Use useEffect to apply filters whenever any filter-related state changes
  useEffect(() => {
    applyFilters();
  }, [selectedStatuses, selectedOrigins, selectedDestinations, selectedCarriers, selectedVessels, selectedConsignees, shipments, startDate, endDate]);


  if (!shipments) {
    return <div>Loading...</div>;
  }

  const navigateToDetails = (shipment) => {
    navigate(`/shipmentDetails/${shipment._id}`, { state: { shipment } });  // Adjust the path if needed
  };


  const calculateIndicators = (shipmentsToCalculate) => {
    const totalShipments = shipmentsToCalculate.length;
    const completedShipments = shipmentsToCalculate.filter(s => s.jobStatus === 'CMP').length;
    const inTransitShipments = totalShipments - completedShipments;

    // Calculate total transit time only for shipments with both consoleAtd and consoleAta present
    const { totalTransitTime, count } = shipmentsToCalculate.reduce((acc, shipment) => {
      if (shipment.consolAtd && shipment.consolAta) {
        const atd = new Date(shipment.consolAtd);
        const ata = new Date(shipment.consolAta);
        // Check if both dates are valid
        if (!isNaN(atd.getTime()) && !isNaN(ata.getTime())) {
          acc.totalTransitTime += ata - atd;
          acc.count += 1;
        }
      }
      return acc;
    }, { totalTransitTime: 0, count: 0 });

    // Calculate average transit time only for shipments with valid transit times
    const averageTransitTime = count > 0 ? (totalTransitTime / count) / (1000 * 60 * 60 * 24) : 0; // in days

    // Shipments at risk are those with consoleAtd greater than originEtd and are not completed
    const shipmentsAtRisk = shipments.filter(s =>
      s.consoleAtd && s.originEtd && new Date(s.consoleAtd) > new Date(s.originEtd) && s.jobStatus !== 'CMP'
    ).length;



    return {
      totalShipments,
      completedShipments,
      inTransitShipments,
      averageTransitTime: averageTransitTime.toFixed(2), // rounding to 2 decimal places
      shipmentsAtRisk
    };
  };

  const indicators = calculateIndicators(filteredShipments);

  const paginatedShipments = filteredShipments.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const handleConsigneeFilterChange = selectedOptions => {
    setSelectedConsignees(selectedOptions);
    applyFilters();
  };  

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    // Parse the date string into a Date object
    const date = new Date(dateString);

    // Use the Intl.DateTimeFormat to format the date
    return new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(date);
  };

  return (
    <>
      <div className="bg-gray-100 min-h-screen"> {/* Light gray background for the whole page */}

        <nav className="w-full flex justify-between items-center px-6 py-4 bg-white shadow">
          <div className="flex items-center">
            <img src={logo} alt="Company logo" className="h-12 mr-2" />
          </div>
          <div className="text-xl font-semibold text-gray-700">
            Shipment Details
          </div>
        </nav>
        <div className="flex justify-center mt-6 mb-4 gap-4 flex-wrap">
          <Select
            isMulti
            options={consigneeOptions}
            value={selectedConsignees} 
            onChange={handleConsigneeFilterChange} 
            className="mb-4"
            placeholder="Filter by Consignee"
            classNamePrefix="select"
          />
          <Select
            isMulti
            options={statusOptions}
            value={selectedStatuses}
            onChange={handleStatusFilterChange}
            className="mb-4"
            placeholder="Filter by Status"
            classNamePrefix="select"
          />
          <Select
            isMulti
            options={originOptions}
            value={selectedOrigins}
            onChange={handleOriginFilterChange}
            className="mb-4"
            placeholder="Filter by Origin"
            classNamePrefix="select"
          />

          <Select
            isMulti
            options={destinationOptions}
            value={selectedDestinations}
            onChange={handleDestinationFilterChange}
            className="mb-4"
            placeholder="Filter by Destination"
            classNamePrefix="select"
          />

          <Select
            isMulti
            options={carrierOptions}
            value={selectedCarriers}
            onChange={handleCarrierFilterChange}
            className="mb-4"
            placeholder="Filter by Carrier"
            classNamePrefix="select"
          />

          <Select
            isMulti
            options={vesselOptions}
            value={selectedVessels}
            onChange={handleVesselFilterChange}
            className="mb-4"
            placeholder="Filter by Vessel/Flight"
            classNamePrefix="select"
          />
          <DatePicker
            selected={startDate}
            onChange={date => setStartDate(date)}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            placeholderText="Start Date"
            customInput={<CustomDateInput placeholder="Start Date" />}
          />

          <DatePicker
            selected={endDate}
            onChange={date => setEndDate(date)}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            minDate={startDate}
            placeholderText="End Date"
            customInput={<CustomDateInput placeholder="End Date" />}
          />
          {/* Add other select components for different filters here */}
        </div>

        <div className="px-4 sm:px-8">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="mb-4 p-2 border rounded w-full"
          />
        </div>
        <MiniDashboard indicators={indicators} shipments={shipments} />

        <div className="container mx-auto px-4 sm:px-8">
          <div className="py-8">
            <Pagination
              total={filteredShipments.length} // Use filteredShipments for pagination
              pageSize={pageSize}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
            <div className="flex flex-row mb-4 justify-between w-full">
            </div>
            <div className="mb-4">
              <div className="grid grid-cols-12 text-sm font-semibold text-gray-700 border-b-2">
                <div className="px-5 py-3 text-center border-r font-bold w-250">Shipment ID</div>
                <div className="px-5 py-3 text-center border-r font-bold w-300">Consignee Name</div>
                <div className="px-5 py-3 text-center border-r font-bold w-250">Status</div>
                <div className="px-5 py-3 text-center border-r font-bold w-300">Origin</div>
                <div className="px-5 py-3 text-center border-r font-bold w-300">Destination</div>
                <div className="px-5 py-3 text-center border-r font-bold w-250">Origin Departure</div>
                <div className="px-5 py-3 text-center border-r font-bold w-250">Destination Arrival</div>
                <div className="px-5 py-3 text-center border-r font-bold w-600">Carrier</div>
                <div className="px-5 py-3 text-center border-r font-bold w-250">Vessel/Flight</div>
                <div className="px-5 py-3 text-center border-r font-bold w-250">Recognized WIP</div>
                <div className="px-5 py-3 text-center border-r font-bold w-250">Recognized Accrual</div>
                <div className="px-5 py-3 text-center border-r font-bold w-250">Job Profit</div>
              </div>
            </div>
            <div className="shadow rounded-lg overflow-hidden">
              {paginatedShipments.map((shipment, index) => (
                <div
                  key={shipment.shipmentId}
                  className={`grid grid-cols-12 items-center p-4 border-b ${index % 2 === 0 ? 'bg-white' : 'bg-gray-100'}`}
                  onClick={() => navigateToDetails(shipment)} // Add the onClick event here
                >
                  <div className="flex items-center px-5 font-bold border-r w-250">
                    <FontAwesomeIcon icon={faTruckMoving} className="text-blue-500 mr-2" />
                    <span className="text-gray-900 whitespace-no-wrap">{shipment.shipmentId}</span>
                  </div>
                  <div className="flex items-center px-5 font-bold border-r w-250">
                    {shipment.consigneeName}
                  </div>
                  <div className="px-5 font-bold border-r text-center">
                    <div className={`inline-flex items-center justify-center px-2 py-1 text-xs leading-5 font-semibold rounded-full ${shipment.jobStatus !== 'CMP' ? 'bg-yellow-200 text-yellow-800' : 'bg-green-200 text-green-800'} w-32`}>
                      <FontAwesomeIcon icon={shipment.jobStatus !== 'CMP' ? faTruckMoving : faCheckCircle} className="mr-2" /> {/* FontAwesome icon */}
                      <span>{shipment.jobStatus !== 'CMP' ? 'In-transit' : 'Completed'}</span>
                    </div>
                  </div>
                  {/* Origin column with adjusted styles */}
                  <div className="px-5 border-r w-300 text-center">
                    <span className="inline-flex items-center justify-center text-black px-2 py-1 text-xs leading-5 font-semibold">
                      {shipment.consignorCity}, {shipment.consignorState}, {shipment.consignorPostCode}
                    </span>
                  </div>

                  {/* Destination column with adjusted styles */}
                  <div className="px-5 border-r w-300 text-center">
                    <span className="inline-flex items-center justify-center  text-black px-2 py-1 text-xs leading-5 font-semibold">
                      {shipment.consigneeCity}, {shipment.consigneeState}, {shipment.consigneePostCode}
                    </span>
                  </div>
                  {/* Origin Departure column with adjusted styles */}
                  <div className="px-5 border-r w-250 text-center">
                    <span className="inline-flex items-center justify-center bg-purple-200 text-purple-800 rounded px-2 py-1 text-xs leading-5 font-semibold">
                      {formatDate(shipment.originEtd)}
                    </span>
                  </div>

                  {/* Destination Arrival column with adjusted styles */}
                  <div className="px-5 border-r w-250 text-center">
                    <span className="inline-flex items-center justify-center bg-purple-200 text-purple-800 rounded px-2 py-1 text-xs leading-5 font-semibold">
                      {formatDate(shipment.destinationEta)}
                    </span>
                  </div>
                  <div className="px-5 border-r w-600 text-center">
                    <span className="inline-flex items-center justify-center  text-black px-2 py-1 text-xs leading-5 font-semibold">
                      {shipment.carrierName}
                    </span>
                  </div>
                  <div className="px-5 border-r w-250 text-center">
                    <span className="inline-flex items-center justify-center  text-black px-2 py-1 text-xs leading-5 font-semibold">{shipment.vessel || shipment.flightVoyage}</span>
                  </div>
                  <div className="px-5 border-r w-600 text-center">
                    <span className="inline-flex items-center justify-center text-black px-2 py-1 text-xs leading-5 font-semibold">
                      {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(shipment.recognizedWip).replace(/(\.00|USD\s)/g, '')}
                    </span>
                  </div>
                  <div className="px-5 border-r w-600 text-center">
                    <span className="inline-flex items-center justify-center text-black px-2 py-1 text-xs leading-5 font-semibold">
                      {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(shipment.recognizedAccrual).replace(/(\.00|USD\s)/g, '')}
                    </span>
                  </div>
                  <div className="px-5 border-r w-600 text-center">
                    <span className="inline-flex items-center justify-center text-black px-2 py-1 text-xs leading-5 font-semibold">
                      {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(shipment.jobProfit).replace(/(\.00|USD\s)/g, '')}
                    </span>
                  </div>

                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ShipmentGrid;
